import React, { useCallback, useEffect, useRef, useState } from 'react';

import { adminContent } from '../../services/api';
import { usePaginatedData } from '../../hooks/usePaginatedData';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { LoadingAnimation } from '../../components/ui/loading-animation';
import { PaginationControls } from '../../components/ui/pagination';
import { useToast } from '../../components/ui/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import type { CourseOption, FlowListFilters } from './FlowBuilder/types';
import type { AdminFlowSummary } from '../../services/api/adminContentClient';

type QuizReadinessIssue = {
  code: string;
  message: string;
  question_id?: number;
  question_key?: string;
};

type QuizReadiness = {
  flow_id: number;
  flow_name: string;
  is_quiz: boolean;
  is_pass_only?: boolean;
  quiz_mode?: 'pass_only' | 'evaluation' | 'graded';
  question_count: number;
  scoreable_question_count: number;
  non_scoreable_question_count: number;
  issue_count: number;
  warning_count: number;
  issues: QuizReadinessIssue[];
  warnings: QuizReadinessIssue[];
  is_ready: boolean;
};

type QuizQuestionOption = {
  id: number;
  front_text: string;
  order?: number;
  is_correct: boolean;
};

type QuizQuestionType = {
  id: number;
  name: string;
};

type QuizQuestion = {
  id: number;
  key?: string;
  text: string;
  metadata?: Record<string, unknown> | null;
  low_text?: string | null;
  high_text?: string | null;
  options?: QuizQuestionOption[];
  question_type?: QuizQuestionType | null;
  flow_order?: number;
  is_required?: boolean;
};

type QuizAttemptRecord = {
  id: number;
  user: number;
  user_username?: string;
  attempt_number: number;
  status: string;
  started_at: string;
  completed_at?: string | null;
  score_percentage?: string | number | null;
  passed?: boolean | null;
};

type QuizAttemptsResponse = {
  count: number;
  attempts: QuizAttemptRecord[];
  flow_id: number;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
};

const parseOptionalEmail = (value: string): string | null | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const isValidEmail = /^[^\s@]+@[^\s@]+$/.test(trimmed);
  if (!isValidEmail) {
    return null;
  }
  return trimmed;
};

const getBooleanAnswerKey = (question: QuizQuestion): string => {
  const metadata = question.metadata && typeof question.metadata === 'object' ? question.metadata : null;
  if (!metadata) {
    return 'Not configured';
  }

  const correctBoolean = metadata.correct_boolean;
  if (typeof correctBoolean === 'boolean') {
    return correctBoolean ? 'True' : 'False';
  }

  return 'Not configured';
};

type QuizMode = 'pass_only' | 'evaluation' | 'graded';

const toQuizModeLabel = (mode: QuizMode): string => {
  if (mode === 'pass_only') {
    return 'Pass-Only Survey';
  }
  if (mode === 'evaluation') {
    return 'Evaluation Quiz';
  }
  return 'Graded Quiz';
};

const deriveQuizModeFromFlow = (flow: Pick<AdminFlowSummary, 'is_quiz' | 'passing_score_percentage'>): QuizMode => {
  if (!flow.is_quiz) {
    return 'graded';
  }
  if (flow.passing_score_percentage === null || flow.passing_score_percentage === undefined) {
    return 'pass_only';
  }
  return Number(flow.passing_score_percentage) === 0 ? 'evaluation' : 'graded';
};

const deriveQuizModeFromReadiness = (readiness: QuizReadiness): QuizMode => {
  if (readiness.quiz_mode === 'pass_only' || readiness.quiz_mode === 'evaluation' || readiness.quiz_mode === 'graded') {
    return readiness.quiz_mode;
  }
  return readiness.is_pass_only ? 'pass_only' : 'graded';
};

type QuizToolsProps = {
  embedded?: boolean;
};

const QuizTools: React.FC<QuizToolsProps> = ({ embedded = false }) => {
  const { addToast } = useToast();
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<AdminFlowSummary | null>(null);
  const selectedFlowIdRef = useRef<number | null>(null);
  const selectionGenerationRef = useRef(0);
  const readinessRequestRef = useRef(0);
  const questionsRequestRef = useRef(0);
  const attemptsRequestRef = useRef(0);
  const resetRequestRef = useRef(0);

  const [readiness, setReadiness] = useState<QuizReadiness | null>(null);
  const [readinessLoading, setReadinessLoading] = useState<boolean>(false);
  const [readinessError, setReadinessError] = useState<string | null>(null);

  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState<boolean>(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);

  const [attemptData, setAttemptData] = useState<QuizAttemptsResponse | null>(null);
  const [attemptsLoading, setAttemptsLoading] = useState<boolean>(false);
  const [attemptsError, setAttemptsError] = useState<string | null>(null);
  const [attemptUserEmailInput, setAttemptUserEmailInput] = useState<string>('');
  const [resetUserEmailInput, setResetUserEmailInput] = useState<string>('');
  const [resettingAttempts, setResettingAttempts] = useState<boolean>(false);

  const fetchFlows = useCallback(
    async ({
      page: nextPage,
      pageSize: nextPageSize,
      search: nextSearch,
      filters: nextFilters,
    }: {
      page: number;
      pageSize: number;
      search?: string;
      filters: FlowListFilters;
    }) => {
      const params: Record<string, string | number | boolean> = {
        page: nextPage,
        page_size: nextPageSize,
        is_active: true,
      };
      if (nextSearch) {
        params.search = nextSearch;
      }
      if (nextFilters.course && nextFilters.course !== 'all') {
        params.course_id = nextFilters.course;
      }

      const response = await adminContent.flows.listSummary(params);
      const data = response.data;

      if (data?.results) {
        const results = data.results as AdminFlowSummary[];
        return {
          data: results,
          total: typeof data.count === 'number' ? data.count : results.length,
        };
      }
      if (Array.isArray(data)) {
        return {
          data: data as AdminFlowSummary[],
          total: data.length,
        };
      }
      return { data: [], total: 0 };
    },
    [],
  );

  const {
    data: flows,
    total,
    loading: flowsLoading,
    error: flowError,
    page,
    pageSize,
    search,
    setSearch,
    filters,
    setFilters,
    setPage,
  } = usePaginatedData<AdminFlowSummary, FlowListFilters>({
    fetcher: fetchFlows,
    initialPageSize: 25,
    initialFilters: { course: 'all' },
  });

  useEffect(() => {
    (async () => {
      try {
        const response = await adminContent.questions.listCourses();
        if (Array.isArray(response.data)) {
          setCourses(response.data as CourseOption[]);
        } else {
          setCourses([]);
        }
      } catch (error) {
        console.error('QuizTools: failed to load courses', error);
        setCourses([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!flowError) {
      return;
    }
    addToast({
      title: 'Failed to load modules',
      description: flowError,
      variant: 'destructive',
    });
  }, [addToast, flowError]);

  useEffect(() => {
    if (flowsLoading) return;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (total === 0 && page !== 1) {
      setPage(1);
    } else if (page > totalPages) {
      setPage(totalPages);
    }
  }, [flowsLoading, page, pageSize, setPage, total]);

  useEffect(() => () => {
    selectedFlowIdRef.current = null;
    selectionGenerationRef.current += 1;
    resetRequestRef.current += 1;
  }, []);

  const selectedFlowId = selectedFlow?.id ?? null;

  useEffect(() => {
    if (!selectedFlowId) {
      return;
    }
    const updatedFlow = flows.find((flow) => flow.id === selectedFlowId);
    if (updatedFlow) {
      setSelectedFlow(updatedFlow);
    }
  }, [flows, selectedFlowId]);

  const loadReadiness = useCallback(async (flowId: number) => {
    if (selectedFlowIdRef.current !== flowId) return;
    const generation = selectionGenerationRef.current;
    const requestId = ++readinessRequestRef.current;
    const isCurrent = () => generation === selectionGenerationRef.current && requestId === readinessRequestRef.current;
    setReadinessLoading(true);
    setReadinessError(null);
    try {
      const response = await adminContent.flows.quizReadiness(flowId);
      if (!isCurrent()) return;
      setReadiness(response.data as QuizReadiness);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('QuizTools: failed to load readiness', error);
      setReadiness(null);
      setReadinessError('Unable to load quiz readiness.');
    } finally {
      if (isCurrent()) setReadinessLoading(false);
    }
  }, []);

  const loadQuestions = useCallback(async (flowId: number) => {
    if (selectedFlowIdRef.current !== flowId) return;
    const generation = selectionGenerationRef.current;
    const requestId = ++questionsRequestRef.current;
    const isCurrent = () => generation === selectionGenerationRef.current && requestId === questionsRequestRef.current;
    setQuestionsLoading(true);
    setQuestionsError(null);
    try {
      const response = await adminContent.flows.quizQuestions(flowId);
      if (!isCurrent()) return;
      if (Array.isArray(response.data)) {
        setQuestions(response.data as QuizQuestion[]);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error('QuizTools: failed to load quiz questions', error);
      setQuestions([]);
      setQuestionsError('Unable to load quiz question details.');
    } finally {
      if (isCurrent()) setQuestionsLoading(false);
    }
  }, []);

  const loadAttempts = useCallback(async (flowId: number, userEmail?: string) => {
    if (selectedFlowIdRef.current !== flowId) return;
    const generation = selectionGenerationRef.current;
    const requestId = ++attemptsRequestRef.current;
    const isCurrent = () => generation === selectionGenerationRef.current && requestId === attemptsRequestRef.current;
    setAttemptsLoading(true);
    setAttemptsError(null);
    try {
      const response = await adminContent.flows.quizAttempts(
        flowId,
        userEmail ? { user_email: userEmail } : undefined,
      );
      if (!isCurrent()) return;
      const payload = response.data as QuizAttemptsResponse;
      setAttemptData({
        count: typeof payload.count === 'number' ? payload.count : 0,
        attempts: Array.isArray(payload.attempts) ? payload.attempts : [],
        flow_id: flowId,
      });
    } catch (error) {
      if (!isCurrent()) return;
      console.error('QuizTools: failed to load quiz attempts', error);
      setAttemptData(null);
      setAttemptsError('Unable to load quiz attempts.');
    } finally {
      if (isCurrent()) setAttemptsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedFlowId) {
      setReadiness(null);
      setQuestions([]);
      setAttemptData(null);
      setReadinessError(null);
      setQuestionsError(null);
      setAttemptsError(null);
      return;
    }
    void loadReadiness(selectedFlowId);
    void loadQuestions(selectedFlowId);
    void loadAttempts(selectedFlowId);
  }, [loadAttempts, loadQuestions, loadReadiness, selectedFlowId]);

  const handleSelectFlow = useCallback((flow: AdminFlowSummary) => {
    if (selectedFlowIdRef.current !== flow.id) {
      selectedFlowIdRef.current = flow.id;
      selectionGenerationRef.current += 1;
      resetRequestRef.current += 1;
      setReadiness(null);
      setQuestions([]);
      setAttemptData(null);
      setReadinessError(null);
      setQuestionsError(null);
      setAttemptsError(null);
      setResettingAttempts(false);
    }
    setSelectedFlow(flow);
    setAttemptUserEmailInput('');
    setResetUserEmailInput('');
  }, []);

  const handleCourseChange = useCallback(
    (value: string) => {
      setFilters({ course: value });
    },
    [setFilters],
  );

  const handleApplyAttemptFilter = useCallback(() => {
    if (!selectedFlowId) {
      return;
    }
    const parsedUserEmail = parseOptionalEmail(attemptUserEmailInput);
    if (parsedUserEmail === null) {
      addToast({
        title: 'Invalid email',
        description: 'Enter a valid user email address.',
        variant: 'destructive',
      });
      return;
    }
    void loadAttempts(selectedFlowId, parsedUserEmail);
  }, [addToast, attemptUserEmailInput, loadAttempts, selectedFlowId]);

  const handleResetAttempts = useCallback(async () => {
    if (!selectedFlowId) {
      return;
    }
    const parsedUserEmail = parseOptionalEmail(resetUserEmailInput);

    if (parsedUserEmail === null) {
      addToast({
        title: 'Invalid email',
        description: 'Enter a valid user email address.',
        variant: 'destructive',
      });
      return;
    }

    if (parsedUserEmail === undefined) {
      addToast({
        title: 'Email required',
        description: 'Enter a user email to reset attempts.',
        variant: 'destructive',
      });
      return;
    }

    const generation = selectionGenerationRef.current;
    const requestId = ++resetRequestRef.current;
    const isCurrent = () => generation === selectionGenerationRef.current && requestId === resetRequestRef.current;
    attemptsRequestRef.current += 1;
    setAttemptsLoading(false);
    setResettingAttempts(true);
    try {
      const response = await adminContent.flows.resetQuizAttempts(selectedFlowId, {
        user_email: parsedUserEmail,
      });
      if (!isCurrent()) return;
      const deletedAttempts =
        typeof response.data?.deleted_attempts === 'number' ? response.data.deleted_attempts : 0;
      const responseUserEmail = response.data?.user_email;
      const identifierLabel =
        typeof responseUserEmail === 'string' && responseUserEmail
          ? responseUserEmail
          : parsedUserEmail;
      addToast({
        title: 'Attempts reset',
        description: `Deleted ${deletedAttempts} attempt(s) for ${identifierLabel}.`,
      });
      const filteredUserEmail = parseOptionalEmail(attemptUserEmailInput);
      void loadAttempts(selectedFlowId, filteredUserEmail === null ? undefined : filteredUserEmail);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('QuizTools: failed to reset attempts', error);
      addToast({
        title: 'Reset failed',
        description: 'Unable to reset quiz attempts.',
        variant: 'destructive',
      });
    } finally {
      if (isCurrent()) setResettingAttempts(false);
    }
  }, [
    addToast,
    attemptUserEmailInput,
    loadAttempts,
    resetUserEmailInput,
    selectedFlowId,
  ]);

  const selectedFlowQuestionCount = selectedFlow?.question_count ?? 0;

  return (
    <section className={embedded ? 'space-y-6' : 'space-y-6 px-6'}>
      {!embedded ? (
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold text-foreground">Quiz QA</h1>
          <p className="text-sm text-muted-foreground">
            Audit quiz readiness, verify answer keys, and inspect or reset user attempts.
          </p>
        </div>
      ) : null}

      <Card disableAnimation maxWidth="none">
        <CardHeader>
          <CardTitle title="Select module" />
        </CardHeader>
        <CardContent variant="custom" className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div className="flex w-full flex-col gap-3 md:flex-row md:items-end">
              <div className="flex-1 min-w-[12.5rem]">
                <Label htmlFor="quiz-tools-flow-search">Search modules</Label>
                <Input
                  id="quiz-tools-flow-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by name or description"
                />
              </div>
              <div className="w-full md:w-60">
                <Label htmlFor="quiz-tools-course-filter">Filter by course</Label>
                <Select value={filters.course ?? 'all'} onValueChange={handleCourseChange}>
                  <SelectTrigger id="quiz-tools-course-filter">
                    <SelectValue placeholder="All courses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All courses</SelectItem>
                    {courses.map((course) => (
                      <SelectItem key={course.id} value={String(course.id)}>
                        {course.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {flowsLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <LoadingAnimation />
            </div>
          ) : (
            <>
              {flowError ? (
                <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {flowError}
                </div>
              ) : null}
              <div className="max-h-[520px] overflow-y-auto rounded-md border border-border/40">
                <table className="w-full text-sm">
                  <thead className="bg-white/10 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">Name</th>
                      <th className="px-3 py-2 font-medium">Description</th>
                      <th className="px-3 py-2 font-medium">Quiz</th>
                      <th className="px-3 py-2 font-medium">Questions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flows.map((flow) => (
                      <tr
                        key={flow.id}
                        className={`cursor-pointer border-t border-white/5 hover:bg-white/10 ${
                          selectedFlowId === flow.id ? 'bg-white/10' : ''
                        }`}
                        onClick={() => handleSelectFlow(flow)}
                      >
                        <td className="px-3 py-2 font-medium">{flow.name}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {flow.description?.slice(0, 120) || '-'}
                        </td>
                        <td className="px-3 py-2">
                          {flow.is_quiz ? (
                            <Badge variant="secondary">Yes</Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {flow.question_count}
                        </td>
                      </tr>
                    ))}
                    {flows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          No modules found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <PaginationControls
                page={page}
                pageSize={pageSize}
                totalCount={total}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {selectedFlow ? (
        <>
          <Card disableAnimation maxWidth="none">
            <CardHeader>
              <CardTitle title={`Module: ${selectedFlow.name}`} />
            </CardHeader>
            <CardContent variant="custom" className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Badge variant={selectedFlow.is_quiz ? 'secondary' : 'outline'}>
                  {selectedFlow.is_quiz ? 'Quiz Enabled' : 'Not Quiz'}
                </Badge>
                {selectedFlow.is_quiz ? (
                  <Badge variant="outline">
                    Mode: {toQuizModeLabel(deriveQuizModeFromFlow(selectedFlow))}
                  </Badge>
                ) : null}
                <Badge variant="outline">Questions: {selectedFlowQuestionCount}</Badge>
                <Badge variant="outline">
                  Passing Score:{' '}
                  {selectedFlow.passing_score_percentage !== null &&
                  selectedFlow.passing_score_percentage !== undefined
                    ? `${selectedFlow.passing_score_percentage}%`
                    : '-'}
                </Badge>
                <Badge variant="outline">
                  Max Attempts:{' '}
                  {selectedFlow.max_attempts !== null && selectedFlow.max_attempts !== undefined
                    ? selectedFlow.max_attempts
                    : 'Unlimited'}
                </Badge>
                <Badge variant="outline">
                  Retake After Pass: {selectedFlow.allow_retake_after_pass ? 'Yes' : 'No'}
                </Badge>
                <Badge variant="outline">
                  Randomize Questions: {selectedFlow.randomize_question_order ? 'Yes' : 'No'}
                </Badge>
                <Badge variant="outline">
                  Randomize Options: {selectedFlow.randomize_option_order ? 'Yes' : 'No'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card disableAnimation maxWidth="none">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle title="Readiness Check" />
                <Button
                  variant="secondary"
                  size="auto"
                  onClick={() => selectedFlowId && void loadReadiness(selectedFlowId)}
                  disabled={!selectedFlowId || readinessLoading}
                >
                  Refresh readiness
                </Button>
              </div>
            </CardHeader>
            <CardContent variant="custom" className="space-y-4">
              {readinessLoading ? (
                <div className="flex min-h-[120px] items-center justify-center">
                  <LoadingAnimation size="sm" />
                </div>
              ) : readinessError ? (
                <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {readinessError}
                </div>
              ) : readiness ? (
                <>
                  <div className="flex flex-wrap gap-2">
                    <Badge
                      variant="outline"
                      className={
                        readiness.is_ready
                          ? 'border-emerald-700/50 bg-emerald-500/15 text-emerald-900'
                          : 'border-red-700/50 bg-red-500/15 text-red-900'
                      }
                    >
                      {readiness.is_ready ? 'Ready' : 'Not Ready'}
                    </Badge>
                    <Badge variant="outline">Questions: {readiness.question_count}</Badge>
                    <Badge variant="outline">
                      Mode: {toQuizModeLabel(deriveQuizModeFromReadiness(readiness))}
                    </Badge>
                    <Badge variant="outline">Scoreable: {readiness.scoreable_question_count}</Badge>
                    <Badge variant="outline">Non-scoreable: {readiness.non_scoreable_question_count}</Badge>
                    <Badge variant={readiness.issue_count > 0 ? 'destructive' : 'outline'}>
                      Issues: {readiness.issue_count}
                    </Badge>
                    <Badge variant={readiness.warning_count > 0 ? 'secondary' : 'outline'}>
                      Warnings: {readiness.warning_count}
                    </Badge>
                  </div>

                  {readiness.issues.length === 0 && readiness.warnings.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No issues or warnings found.</p>
                  ) : (
                    <div
                      className={`grid gap-4 ${
                        readiness.issues.length > 0 && readiness.warnings.length > 0
                          ? 'lg:grid-cols-2'
                          : 'lg:grid-cols-1'
                      }`}
                    >
                      {readiness.issues.length > 0 ? (
                        <div className="space-y-2 rounded border border-destructive/30 bg-destructive/10 p-3">
                          <p className="text-sm font-semibold text-destructive">Issues</p>
                          <ul className="space-y-1 text-xs text-foreground">
                            {readiness.issues.map((issue, index) => (
                              <li key={`${issue.code}-${issue.question_id ?? index}`}>
                                {issue.message}
                                {issue.question_key ? ` (${issue.question_key})` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      {readiness.warnings.length > 0 ? (
                        <div className="space-y-2 rounded border border-border/40 bg-white/5 p-3">
                          <p className="text-sm font-semibold text-foreground">Warnings</p>
                          <ul className="space-y-1 text-xs text-foreground">
                            {readiness.warnings.map((warning, index) => (
                              <li key={`${warning.code}-${warning.question_id ?? index}`}>
                                {warning.message}
                                {warning.question_key ? ` (${warning.question_key})` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No readiness data loaded.</p>
              )}
            </CardContent>
          </Card>

          <Card disableAnimation maxWidth="none">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle title="Answer Key Audit" />
                <Button
                  variant="secondary"
                  size="auto"
                  onClick={() => selectedFlowId && void loadQuestions(selectedFlowId)}
                  disabled={!selectedFlowId || questionsLoading}
                >
                  Refresh questions
                </Button>
              </div>
            </CardHeader>
            <CardContent variant="custom" className="space-y-4">
              {questionsLoading ? (
                <div className="flex min-h-[160px] items-center justify-center">
                  <LoadingAnimation size="sm" />
                </div>
              ) : questionsError ? (
                <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {questionsError}
                </div>
              ) : questions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No question data available.</p>
              ) : (
                <div className="max-h-[640px] overflow-y-auto rounded-md border border-border/40">
                  <table className="w-full text-sm">
                    <thead className="bg-white/10 text-left text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">Order</th>
                        <th className="px-3 py-2 font-medium">Question</th>
                        <th className="px-3 py-2 font-medium">Type</th>
                        <th className="px-3 py-2 font-medium">Answer key</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.map((question) => {
                        const questionType = question.question_type?.name ?? 'unknown';
                        const sortedOptions = (question.options ?? []).slice().sort((a, b) => {
                          const orderA = typeof a.order === 'number' ? a.order : 0;
                          const orderB = typeof b.order === 'number' ? b.order : 0;
                          return orderA - orderB;
                        });

                        return (
                          <tr key={question.id} className="border-t border-white/5 align-top">
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {question.flow_order ?? '-'}
                            </td>
                            <td className="px-3 py-2">
                              <p className="font-medium">{question.key ?? `Q${question.id}`}</p>
                              <p className="text-xs text-muted-foreground">{question.text}</p>
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{questionType}</td>
                            <td className="px-3 py-2">
                              {questionType === 'boolean' ? (
                                <span className="text-xs">{getBooleanAnswerKey(question)}</span>
                              ) : questionType === 'multiple_choice' || questionType === 'multiple_select' ? (
                                <ul className="space-y-1 text-xs">
                                  {sortedOptions.map((option) => (
                                    <li
                                      key={option.id}
                                      className={option.is_correct ? 'text-foreground' : 'text-muted-foreground'}
                                    >
                                      {option.is_correct ? 'Correct:' : 'Incorrect:'} {option.front_text}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-xs text-muted-foreground">Non-scoreable</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card disableAnimation maxWidth="none">
            <CardHeader>
              <CardTitle title="Attempt Tools" />
            </CardHeader>
            <CardContent variant="custom" className="space-y-4">
              <div className="grid gap-3 lg:grid-cols-[1fr_auto_auto]">
                <div className="space-y-1">
                  <Label htmlFor="quiz-attempt-user-email">Filter attempts by user email (optional)</Label>
                  <Input
                    id="quiz-attempt-user-email"
                    value={attemptUserEmailInput}
                    onChange={(event) => setAttemptUserEmailInput(event.target.value)}
                    placeholder="e.g. student@example.com"
                  />
                </div>
                <Button
                  className="self-end"
                  variant="secondary"
                  onClick={handleApplyAttemptFilter}
                  disabled={!selectedFlowId || attemptsLoading}
                >
                  Apply filter
                </Button>
                <Button
                  className="self-end"
                  variant="ghost"
                  onClick={() => {
                    setAttemptUserEmailInput('');
                    if (selectedFlowId) {
                      void loadAttempts(selectedFlowId);
                    }
                  }}
                  disabled={!selectedFlowId || attemptsLoading}
                >
                  Clear filter
                </Button>
              </div>

              <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
                <div className="space-y-1">
                  <Label htmlFor="quiz-reset-user-email">Reset attempts for user email</Label>
                  <Input
                    id="quiz-reset-user-email"
                    value={resetUserEmailInput}
                    onChange={(event) => setResetUserEmailInput(event.target.value)}
                    placeholder="e.g. student@example.com"
                  />
                </div>
                <Button
                  className="self-end"
                  variant="default"
                  onClick={() => void handleResetAttempts()}
                  disabled={!selectedFlowId || resettingAttempts}
                >
                  {resettingAttempts ? 'Resetting...' : 'Reset attempts'}
                </Button>
              </div>

              {attemptsLoading ? (
                <div className="flex min-h-[160px] items-center justify-center">
                  <LoadingAnimation size="sm" />
                </div>
              ) : attemptsError ? (
                <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {attemptsError}
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Total attempts in result: {attemptData?.count ?? 0}
                  </p>
                  <div className="max-h-[520px] overflow-y-auto rounded-md border border-border/40">
                    <table className="w-full text-sm">
                      <thead className="bg-white/10 text-left text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 font-medium">Attempt</th>
                          <th className="px-3 py-2 font-medium">User</th>
                          <th className="px-3 py-2 font-medium">Status</th>
                          <th className="px-3 py-2 font-medium">Score</th>
                          <th className="px-3 py-2 font-medium">Passed</th>
                          <th className="px-3 py-2 font-medium">Started</th>
                          <th className="px-3 py-2 font-medium">Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(attemptData?.attempts ?? []).map((attempt) => (
                          <tr key={attempt.id} className="border-t border-white/5">
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              #{attempt.attempt_number}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {attempt.user_username || `User ${attempt.user}`}
                            </td>
                            <td className="px-3 py-2">
                              <Badge variant="outline">{attempt.status}</Badge>
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {attempt.score_percentage ?? '-'}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {attempt.passed === null || attempt.passed === undefined
                                ? '-'
                                : attempt.passed
                                ? 'Yes'
                                : 'No'}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {formatDateTime(attempt.started_at)}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {formatDateTime(attempt.completed_at)}
                            </td>
                          </tr>
                        ))}
                        {(attemptData?.attempts ?? []).length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                              No attempts found for this module/filter.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card disableAnimation maxWidth="none">
          <CardContent className="flex min-h-[220px] items-center justify-center text-sm text-muted-foreground">
            Select a module to inspect quiz readiness, answer keys, and attempts.
          </CardContent>
        </Card>
      )}
    </section>
  );
};

export default QuizTools;
